import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Save, Info, Plus, DollarSign, Package, Trash2, Edit, AlertTriangle,
  Calendar, RotateCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

const MOIS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// 🆕 [Phase 20 — 2026-05-31] Fréquences refondues — 1 mécanisme unique via cycles
const FREQUENCE_OPTIONS = [
  { value: "annuel", label: "Annuel (1 cycle/an)", nb: 1 },
  { value: "semestriel", label: "Semestriel (2 cycles/an)", nb: 2 },
  { value: "trimestriel", label: "Trimestriel (4 cycles/an)", nb: 4 },
  { value: "saisonnier", label: "Saisonnier (1 cycle, fenêtre saison)", nb: 1 },
  { value: "sur_demande", label: "Sur demande (pas de cycle)", nb: 1 },
  { value: "jamais", label: "Jamais (unique)", nb: 1 },
];

const TEMPLATES_CYCLES = {
  annuel: [{ nom: "", mois_debut: 1, mois_fin: 12, date_debut: "1er janvier" }],
  semestriel: [
    { nom: "Cycle 1", mois_debut: 1, mois_fin: 6, date_debut: "1er janvier" },
    { nom: "Cycle 2", mois_debut: 7, mois_fin: 12, date_debut: "1er juillet" },
  ],
  trimestriel: [
    { nom: "T1", mois_debut: 1, mois_fin: 3, date_debut: "1er janvier" },
    { nom: "T2", mois_debut: 4, mois_fin: 6, date_debut: "1er avril" },
    { nom: "T3", mois_debut: 7, mois_fin: 9, date_debut: "1er juillet" },
    { nom: "T4", mois_debut: 10, mois_fin: 12, date_debut: "1er octobre" },
  ],
  saisonnier: [{ nom: "Saison", mois_debut: 5, mois_fin: 10, date_debut: "" }],
  sur_demande: [{ nom: "", mois_debut: 1, mois_fin: 12, date_debut: "" }],
  jamais: [{ nom: "", mois_debut: 1, mois_fin: 12, date_debut: "" }],
};

const emptyCycle = (template = {}) => ({
  ordre: 1,
  nom: template.nom || "",
  date_debut: template.date_debut || "",
  mois_debut: template.mois_debut || 1,
  mois_fin: template.mois_fin || 12,
  quota_permanent: 0,
  quota_saisonnier: 0,
  quota_sous_traitant: 0,
  quota_admin: 0,
  notes: "",
  reset_par_cycle: "unique",
});

// Construit des cycles à partir des templates en copiant les quotas du cycle source (cycle 1)
const buildCyclesForFrequency = (frequence, sourceCycle = null) => {
  const templates = TEMPLATES_CYCLES[frequence] || TEMPLATES_CYCLES.annuel;
  return templates.map((t, i) => ({
    ...emptyCycle(t),
    ordre: i + 1,
    // Pour saisonnier, reset_par_cycle peut être "mensuel" pour respecter l'ancien comportement
    reset_par_cycle: frequence === "saisonnier" ? "mensuel" : "unique",
    quota_permanent: sourceCycle?.quota_permanent || 0,
    quota_saisonnier: sourceCycle?.quota_saisonnier || 0,
    quota_sous_traitant: sourceCycle?.quota_sous_traitant || 0,
    quota_admin: sourceCycle?.quota_admin || 0,
  }));
};

// Dérive les cycles depuis le format DB (cycles ou legacy)
const derivePoliciesCycles = (politique) => {
  if (Array.isArray(politique.cycles) && politique.cycles.length > 0) {
    return politique.cycles;
  }
  // Legacy → reconstruction transparente
  const freq = politique.frequence_remise === "mensuel_saisonnier" ? "saisonnier"
    : (politique.frequence_remise || "annuel");
  const principal = {
    quota_permanent: politique.quota_annuel_defaut || 0,
    quota_saisonnier: politique.quota_annuel_saisonnier || 0,
    quota_sous_traitant: politique.quota_annuel_sous_traitant || 0,
    quota_admin: politique.quota_annuel_admin || 0,
  };
  const templates = TEMPLATES_CYCLES[freq] || TEMPLATES_CYCLES.annuel;
  const cycles = templates.map((t, i) => ({
    ...emptyCycle(t),
    ordre: i + 1,
    reset_par_cycle: freq === "saisonnier" ? "mensuel" : "unique",
    mois_debut: freq === "saisonnier" ? (politique.mois_debut || 5) : t.mois_debut,
    mois_fin: freq === "saisonnier" ? (politique.mois_fin || 10) : t.mois_fin,
    date_debut: i === 0 ? (politique.date_remise_a_zero || t.date_debut) : t.date_debut,
    notes: i === 0 ? (politique.notes || "") : "",
    ...principal,
  }));
  // Si règles_additionnelles → on les ajoute comme cycles supplémentaires
  (politique.regles_additionnelles || []).forEach((r, idx) => {
    cycles.push({
      ordre: cycles.length + 1,
      nom: r.nom || `Règle ${idx + 1}`,
      date_debut: "",
      mois_debut: r.mois_debut || 1,
      mois_fin: r.mois_fin || 12,
      reset_par_cycle: r.frequence_remise === "mensuel_saisonnier" ? "mensuel" : "unique",
      quota_permanent: r.quota_permanent || 0,
      quota_saisonnier: r.quota_saisonnier || 0,
      quota_sous_traitant: r.quota_sous_traitant || 0,
      quota_admin: r.quota_admin || 0,
      notes: r.notes || "",
    });
  });
  return cycles;
};

const emptyPolitique = () => ({
  vetement_id: '',
  vetement_nom: '',
  vetement_type: '',
  prix_unitaire: 0,
  obligatoire: false,
  // 🆕 [Phase 67] Par défaut, un nouvel article a des grandeurs (S/M/L/XL/XXL).
  // Décochable pour les articles "unisize" (gants, pinceaux, sacs, etc.).
  a_grandeurs: true,
  notes: '',
  frequence: 'annuel',
  cycles: buildCyclesForFrequency('annuel'),
  __isNew: true,
});

export const PolitiquesVetements = () => {
  const [loading, setLoading] = useState(true);
  const [politiques, setPolitiques] = useState([]);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingPolitique, setEditingPolitique] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [employeeCounts, setEmployeeCounts] = useState({ terrain_permanent: 0, terrain_saisonnier: 0, sous_traitant: 0, admin: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [polRes, modelsRes, empRes] = await Promise.all([
        axios.get(`${api.baseUrl}/vetements/politiques`),
        axios.get(`${api.baseUrl}/vetements/models`),
        axios.get(`${api.baseUrl}/employees`),
      ]);
      setPolitiques(polRes.data.politiques || []);
      setModels(modelsRes.data.models || []);
      const emps = (empRes.data.employees || empRes.data || []).filter(e => e.est_actif !== false);
      const counts = { terrain_permanent: 0, terrain_saisonnier: 0, sous_traitant: 0, admin: 0 };
      emps.forEach(e => {
        const type = e.type_employe || 'terrain';
        const perm = e.permanence || 'permanent';
        const dept = e.departement || '';
        const role = e.role_terrain || ''; // Cat H — `e.role_terrain` = classification métier de l'employé (donnée), pas RBAC utilisateur connecté.
        if (role === 'sous_traitant' || dept === 'LMDS') counts.sous_traitant++;  // E3b-whitelist : catégorie de données
        else if (type === 'admin') counts.admin++;
        else if (perm === 'saisonnier') counts.terrain_saisonnier++;
        else counts.terrain_permanent++;
      });
      setEmployeeCounts(counts);
    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => setEditingPolitique(emptyPolitique());

  const openEditDialog = (politique) => {
    const model = models.find(m => m.id === politique.vetement_id);
    const cycles = derivePoliciesCycles(politique);
    // Si politique migrée → frequence présente ; sinon on dérive depuis frequence_remise
    let frequence = politique.frequence;
    if (!frequence) {
      const old = politique.frequence_remise || 'annuel';
      frequence = old === 'mensuel_saisonnier' ? 'saisonnier' : old;
    }
    setEditingPolitique({
      vetement_id: politique.vetement_id,
      vetement_nom: model?.nom || politique.vetement_nom || '',
      vetement_type: model?.type || politique.vetement_type || '',
      prix_unitaire: model?.prix_unitaire ?? politique.prix_unitaire ?? 0,
      obligatoire: !!politique.obligatoire,
      // 🆕 [Phase 67] Lecture du toggle a_grandeurs (default true rétro-compat)
      a_grandeurs: model?.a_grandeurs !== false,
      notes: politique.notes || '',
      frequence,
      cycles,
      __isNew: false,
    });
  };

  const closeDialog = () => setEditingPolitique(null);

  const updateField = (field, value) => {
    setEditingPolitique(prev => ({ ...prev, [field]: value }));
  };

  // 🆕 [Phase 20] Nombre de cycles par an = simple longueur du tableau cycles
  const cyclesParAn = (politique) => {
    if (!politique) return 1;
    return Array.isArray(politique.cycles) ? politique.cycles.length : 1;
  };

  // Budget pour 1 employé sur 1 an pour un rôle donné
  //   = prix × Σ(quotas du rôle sur tous les cycles)
  const computeRoleBudget = (roleKey) => {
    if (!editingPolitique) return 0;
    const prix = parseFloat(editingPolitique.prix_unitaire) || 0;
    const sumQuotas = (editingPolitique.cycles || []).reduce(
      (acc, c) => acc + (parseInt(c[roleKey]) || 0), 0
    );
    return prix * sumQuotas;
  };

  // 🆕 [Phase 20] Helpers cycles (mise à jour d'un cycle, ajout, suppression)
  const updateCycle = (idx, field, value) => {
    setEditingPolitique(prev => {
      const next = { ...prev, cycles: [...(prev.cycles || [])] };
      next.cycles[idx] = { ...next.cycles[idx], [field]: value };
      return next;
    });
  };

  // Quand la fréquence change → regénérer les cycles (en copiant les quotas du 1er cycle)
  const changeFrequence = (freq) => {
    setEditingPolitique(prev => {
      const source = (prev?.cycles || [])[0] || null;
      return {
        ...prev,
        frequence: freq,
        cycles: buildCyclesForFrequency(freq, source),
      };
    });
  };

  const duplicateCyclePrecedent = (idx) => {
    if (idx === 0) return;
    setEditingPolitique(prev => {
      const next = { ...prev, cycles: [...(prev.cycles || [])] };
      const prevCycle = next.cycles[idx - 1];
      next.cycles[idx] = {
        ...next.cycles[idx],
        quota_permanent: prevCycle.quota_permanent,
        quota_saisonnier: prevCycle.quota_saisonnier,
        quota_sous_traitant: prevCycle.quota_sous_traitant,
        quota_admin: prevCycle.quota_admin,
      };
      return next;
    });
  };

  const savePolitique = async () => {
    if (!editingPolitique) return;
    const nom = (editingPolitique.vetement_nom || '').trim();
    if (!nom) { toast.error('Le nom de l\'item est requis'); return; }

    setSaving(true);
    try {
      // 1) Créer/Renommer le modèle (catalogue)
      let vetement_id = editingPolitique.vetement_id;
      const prix = parseFloat(editingPolitique.prix_unitaire) || 0;

      if (editingPolitique.__isNew || !vetement_id) {
        vetement_id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        await axios.post(`${api.baseUrl}/vetements/models`, {
          id: vetement_id,
          nom,
          type: editingPolitique.vetement_type || nom,
          description: nom,
          prix_unitaire: prix,
          // 🆕 [Phase 67] persist a_grandeurs (default true)
          a_grandeurs: editingPolitique.a_grandeurs !== false,
        });
      } else {
        // Renommer le modèle si le nom a changé + mettre à jour le prix
        await axios.put(`${api.baseUrl}/vetements/models/${vetement_id}`, {
          nom,
          prix_unitaire: prix,
          obligatoire: !!editingPolitique.obligatoire,
          // 🆕 [Phase 67] toggle grandeurs
          a_grandeurs: editingPolitique.a_grandeurs !== false,
        });
      }

      // 3) Save politique — 🆕 [Phase 20] envoie cycles unifiés
      const cyclesPayload = (editingPolitique.cycles || []).map((c, i) => ({
        ordre: i + 1,
        nom: c.nom || '',
        date_debut: c.date_debut || '',
        mois_debut: parseInt(c.mois_debut) || 1,
        mois_fin: parseInt(c.mois_fin) || 12,
        quota_permanent: parseInt(c.quota_permanent) || 0,
        quota_saisonnier: parseInt(c.quota_saisonnier) || 0,
        quota_sous_traitant: parseInt(c.quota_sous_traitant) || 0,
        quota_admin: parseInt(c.quota_admin) || 0,
        notes: c.notes || '',
        reset_par_cycle: c.reset_par_cycle || 'unique',
      }));
      await axios.post(`${api.baseUrl}/vetements/politiques`, {
        vetement_id,
        frequence: editingPolitique.frequence || 'annuel',
        cycles: cyclesPayload,
        obligatoire: !!editingPolitique.obligatoire,
        notes: editingPolitique.notes || '',
      });

      toast.success(editingPolitique.__isNew
        ? `✅ Item "${nom}" créé`
        : `✅ Politique "${nom}" mise à jour`);
      closeDialog();
      loadData();
    } catch (error) {
      console.error('Erreur save politique:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deletePolitique = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${api.baseUrl}/vetements/politiques/${confirmDelete.vetement_id}`);
      toast.success(`🗑️ "${confirmDelete.vetement_nom}" supprimé`);
      setConfirmDelete(null);
      closeDialog();
      loadData();
    } catch (e) {
      console.error('Erreur suppression:', e);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const initPolitiques = async () => {
    const defaults = models.map(m => ({
      vetement_id: m.id,
      quota_annuel_defaut: (m.type === "T-shirt") ? 8 : (m.type === "Cotton Ouaté") ? 2 : (m.type === "Pantalon") ? 3 : 1,
      obligatoire: m.obligatoire,
      notes: "",
    }));
    try {
      for (const pol of defaults) {
        await axios.post(`${api.baseUrl}/vetements/politiques`, pol);
      }
      toast.success("✅ Politiques initialisées");
      loadData();
    } catch (e) {
      console.error("Erreur:", e);
      toast.error("Erreur lors de l'initialisation");
    }
  };

  const resyncAll = async () => {
    if (!window.confirm("Cela va resynchroniser les quotas de TOUS les employés actifs selon les politiques actuelles. Les quantités déjà prises seront conservées. Continuer ?")) return;
    try {
      const res = await axios.post(`${api.baseUrl}/vetements/quotas/resync-all`, {});
      if (res.status === 200) {
        toast.success(`✅ ${res.data.employes_traites} employés resynchronisés (${res.data.quotas_updated} quotas mis à jour)`);
      }
    } catch {
      toast.error("Erreur lors du resync");
    }
  };

  if (loading) return <div className="text-center py-8">Chargement…</div>;

  // 🆕 [Phase 30 — Fix P0] Mapping rôle UI → noms des champs cycles
  // Les cycles utilisent quota_permanent / quota_saisonnier / quota_sous_traitant / quota_admin
  // PAS quota_annuel_defaut etc. (qui étaient les noms scalaires legacy).
  const ROLE_FIELDS_MAP = {
    terrain_permanent: ['quota_permanent', 'quota_annuel_defaut'],
    terrain_saisonnier: ['quota_saisonnier', 'quota_annuel_saisonnier'],
    sous_traitant: ['quota_sous_traitant', 'quota_annuel_sous_traitant'],
    admin: ['quota_admin', 'quota_annuel_admin'],
  };

  // Helper unifié : somme du quota d'un rôle sur tous les cycles d'une politique.
  //   roleField = nom de champ DANS les cycles (ex: 'quota_permanent') OU
  //               nom de champ legacy scalaire (ex: 'quota_annuel_defaut')
  // Essaie d'abord roleField puis tous les alias du mapping.
  const sommeQuotaRolePourPolitique = (politique, roleField) => {
    if (!politique) return 0;
    // Trouver la liste de champs candidats (priorité au champ demandé)
    const candidates = [roleField];
    for (const aliases of Object.values(ROLE_FIELDS_MAP)) {
      if (aliases.includes(roleField)) {
        for (const a of aliases) {
          if (!candidates.includes(a)) candidates.push(a);
        }
        break;
      }
    }
    // Lecture cycles[] : somme du premier champ candidat trouvé (cycle peut
    // avoir plusieurs alias selon migration partielle).
    if (Array.isArray(politique.cycles) && politique.cycles.length > 0) {
      return politique.cycles.reduce((s, c) => {
        for (const k of candidates) {
          if (c[k] != null) return s + (parseInt(c[k]) || 0);
        }
        return s;
      }, 0);
    }
    // Fallback legacy scalaire
    for (const k of candidates) {
      if (politique[k] != null) return parseInt(politique[k]) || 0;
    }
    return 0;
  };

  // Budget total annuel par rôle (somme sur toutes les politiques)
  //   = Σ_pol(prix × Σ_cycle(quota_role)) × nb_emp_role
  const totalRoleBudget = (key, countKey) => {
    const n = employeeCounts[countKey] || 0;
    const sumPerEmp = politiques.reduce((s, p) => {
      const model = models.find(m => m.id === p.vetement_id);
      const px = model?.prix_unitaire || 0;
      return s + px * sommeQuotaRolePourPolitique(p, key);
    }, 0);
    return { perEmp: sumPerEmp, total: sumPerEmp * n, n };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Politique Quotas</h1>
          <p className="text-slate-500 mt-1">
            Articles sous quotas (vêtements, équipements, fournitures) — quotas par rôle, fréquence de remise, budgets estimés.
          </p>
        </div>
        <div className="flex gap-2">
          {politiques.length === 0 && models.length > 0 && (
            <Button onClick={initPolitiques} className="bg-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              Initialiser
            </Button>
          )}
          <Button variant="outline" onClick={resyncAll} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-resync-all-employees">
            <RotateCw className="w-4 h-4 mr-2" />
            Appliquer à tous les employés
          </Button>
          <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-quota-item">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un item
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Comment ça fonctionne :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Admin voit :</strong> quotas en unités par rôle + prix unitaire + budget annuel estimé.</li>
                <li><strong>Employé voit :</strong> uniquement ses items restants et la date de prochaine remise à zéro.</li>
                <li>Les quotas se réinitialisent automatiquement selon la <strong>fréquence de remise</strong> choisie (annuel, mensuel saisonnier, etc.).</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget summary */}
      {politiques.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <p className="text-sm font-semibold text-emerald-800">Budget annuel estimé — Articles sous quotas</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {[
                { key: 'quota_annuel_defaut', label: 'Terrain Permanent', color: 'blue', countKey: 'terrain_permanent' },
                { key: 'quota_annuel_saisonnier', label: 'Terrain Saisonnier', color: 'amber', countKey: 'terrain_saisonnier' },
                { key: 'quota_annuel_sous_traitant', label: 'Sous-traitants', color: 'emerald', countKey: 'sous_traitant' },
                { key: 'quota_annuel_admin', label: 'Admin', color: 'purple', countKey: 'admin' },
              ].map(({ key, label, color, countKey }) => {
                const { perEmp, total, n } = totalRoleBudget(key, countKey);
                return (
                  <div key={key} className={`bg-white rounded-lg border border-${color}-200 p-3 text-center`}>
                    <p className={`text-[10px] text-${color}-600 font-medium uppercase`}>{label}</p>
                    <p className={`text-lg font-bold text-${color}-800 mt-1`}>{total.toFixed(0)} $</p>
                    <p className="text-[10px] text-slate-400">{n} emp. × {perEmp.toFixed(0)}$/emp</p>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-emerald-200">
              <span className="text-sm font-bold text-emerald-900">Budget total annuel</span>
              <span className="text-xl font-bold text-emerald-700">
                {[
                  { key: 'quota_annuel_defaut', countKey: 'terrain_permanent' },
                  { key: 'quota_annuel_saisonnier', countKey: 'terrain_saisonnier' },
                  { key: 'quota_annuel_sous_traitant', countKey: 'sous_traitant' },
                  { key: 'quota_annuel_admin', countKey: 'admin' },
                ].reduce((tot, { key, countKey }) => tot + totalRoleBudget(key, countKey).total, 0).toFixed(0)} $
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste */}
      {politiques.length === 0 && models.length === 0 ? (
        <Card><CardContent className="pt-6 text-center text-slate-500">
          <p>Aucun item sous quotas configuré</p>
          <p className="text-sm mt-1">Cliquez sur <strong>« Ajouter un item »</strong> pour démarrer.</p>
        </CardContent></Card>
      ) : politiques.length === 0 ? (
        <Card><CardContent className="pt-6 text-center text-slate-500">
          <p>Aucune politique configurée</p>
          <p className="text-sm mt-1">Cliquez sur <strong>« Initialiser »</strong> pour créer les politiques par défaut.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Articles sous quotas</CardTitle>
            <CardDescription>Cliquez sur un item pour modifier toutes ses propriétés (nom, quotas, fréquence, prix).</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {politiques.map((politique) => {
                const model = models.find(m => m.id === politique.vetement_id);
                const prix = model?.prix_unitaire || 0;
                const cycles = cyclesParAn(politique);
                // 🆕 [Phase 29 — Issue A] Quotas par rôle calculés à partir des cycles
                const qPerm = sommeQuotaRolePourPolitique(politique, 'quota_annuel_defaut') ||
                              sommeQuotaRolePourPolitique(politique, 'quota_permanent');
                const qSais = sommeQuotaRolePourPolitique(politique, 'quota_annuel_saisonnier') ||
                              sommeQuotaRolePourPolitique(politique, 'quota_saisonnier');
                const qST = sommeQuotaRolePourPolitique(politique, 'quota_annuel_sous_traitant') ||
                            sommeQuotaRolePourPolitique(politique, 'quota_sous_traitant');
                const qAdm = sommeQuotaRolePourPolitique(politique, 'quota_annuel_admin') ||
                             sommeQuotaRolePourPolitique(politique, 'quota_admin');
                // Budget RÉEL : Σ_role(quota_sur_cycles × prix × nb_employés_role)
                const budgetLigne =
                  prix * (
                    qPerm * (employeeCounts.terrain_permanent || 0)
                    + qSais * (employeeCounts.terrain_saisonnier || 0)
                    + qST * (employeeCounts.sous_traitant || 0)
                    + qAdm * (employeeCounts.admin || 0)
                  );
                const freqLabel = FREQUENCE_OPTIONS.find(o => o.value === politique.frequence_remise)?.label || 'Annuel';
                return (
                  <div
                    key={politique.vetement_id}
                    className="flex flex-wrap items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 hover:border-blue-300 transition cursor-pointer"
                    onClick={() => openEditDialog(politique)}
                    data-testid={`politique-row-${politique.vetement_id}`}
                  >
                    <Package className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-medium text-slate-900">{politique.vetement_nom}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[11px] text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {freqLabel}
                        </span>
                        {politique.obligatoire && (
                          <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 rounded px-1.5 py-0.5">Obligatoire</span>
                        )}
                        {/* 🆕 2026-05-23 — Indicateur règles supplémentaires */}
                        {Array.isArray(politique.regles_additionnelles) && politique.regles_additionnelles.length > 0 && (
                          <span
                            className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"
                            title={politique.regles_additionnelles.map(r => r.nom || 'Règle').join(', ')}
                            data-testid={`badge-regles-supp-${politique.vetement_id}`}
                          >
                            +{politique.regles_additionnelles.length} règle{politique.regles_additionnelles.length > 1 ? 's' : ''} saisonnière{politique.regles_additionnelles.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* 🆕 [Phase 29 — Issue A] Quotas par rôle = somme cycles (fallback legacy) */}
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">P : <strong>{qPerm}</strong></span>
                      <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">S : <strong>{qSais}</strong></span>
                      <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ST : <strong>{qST}</strong></span>
                      <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200">A : <strong>{qAdm}</strong></span>
                    </div>
                    <div className="w-24 text-center">
                      <p className="text-[10px] text-slate-400">Prix unitaire</p>
                      <p className="text-sm font-semibold text-slate-700">{prix.toFixed(2)} $</p>
                    </div>
                    <div className="w-24 text-center">
                      <p className="text-[10px] text-slate-400">Budget/an</p>
                      <p className="text-sm font-bold text-emerald-700">{budgetLigne.toFixed(2)} $</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="outline" className="h-8 px-2"
                        onClick={(e) => { e.stopPropagation(); openEditDialog(politique); }}
                        title="Modifier"
                        data-testid={`btn-edit-${politique.vetement_id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-8 px-2 text-red-600 hover:bg-red-50 border-red-200"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(politique); }}
                        title="Supprimer"
                        data-testid={`btn-delete-${politique.vetement_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <p className="text-sm text-amber-800">
            <strong>Note :</strong> modifier une politique n'affecte PAS les quotas déjà initialisés pour les employés existants. Cliquez sur <strong>« Appliquer à tous les employés »</strong> pour pousser les changements à tout le monde (les quantités déjà prises sont préservées).
          </p>
        </CardContent>
      </Card>

      {/* Dialog unifié — Création + Modification */}
      <Dialog open={!!editingPolitique} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPolitique?.__isNew ? 'Nouvel article sous quota' : `Modifier — ${editingPolitique?.vetement_nom || ''}`}
            </DialogTitle>
          </DialogHeader>
          {editingPolitique && (
            <div className="space-y-4">
              {/* Nom + obligatoire */}
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="col-span-2">
                  <Label>Nom de l'item *</Label>
                  <Input
                    value={editingPolitique.vetement_nom || ''}
                    onChange={e => updateField('vetement_nom', e.target.value)}
                    placeholder="Ex : T-shirt logo, Gants, Pinceau 4po..."
                    data-testid="dialog-item-name"
                  />
                </div>
                <div className="flex items-center gap-2 pb-1">
                  <Switch
                    checked={!!editingPolitique.obligatoire}
                    onCheckedChange={(c) => updateField('obligatoire', c)}
                    data-testid="dialog-item-obligatoire"
                  />
                  <Label className="text-xs">Item obligatoire</Label>
                </div>
              </div>

              {/* 🆕 [Phase 67] Toggle "Cet article a des grandeurs" */}
              <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-slate-200 bg-slate-50/60">
                <div className="flex items-center gap-2">
                  <span className="text-base">📏</span>
                  <div>
                    <Label className="text-sm font-medium">Cet article a des grandeurs (S/M/L/XL/XXL)</Label>
                    <p className="text-[11px] text-slate-500">
                      Décochez pour les articles unisize (gants, pinceaux, sacs, etc.). Le sélecteur Taille sera masqué lors de la remise.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingPolitique.a_grandeurs !== false}
                  onCheckedChange={(c) => updateField('a_grandeurs', c)}
                  data-testid="dialog-item-a-grandeurs"
                />
              </div>

              {/* 🆕 [Phase 20] Prix + Fréquence unifiée */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> Prix unitaire ($)</Label>
                  <Input
                    type="number" min={0} step="0.01"
                    value={editingPolitique.prix_unitaire ?? 0}
                    onChange={e => updateField('prix_unitaire', e.target.value)}
                    placeholder="0.00"
                    data-testid="dialog-prix-unitaire"
                  />
                </div>
                <div>
                  <Label>Fréquence</Label>
                  <Select
                    value={editingPolitique.frequence || 'annuel'}
                    onValueChange={changeFrequence}
                  >
                    <SelectTrigger data-testid="dialog-frequence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCE_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 🆕 [Phase 20] Section UNIFIÉE — Cycles dynamiques selon la fréquence.
                  Annuel = 1 cycle, Semestriel = 2, Trimestriel = 4, Saisonnier = 1 (avec fenêtre). */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Cycles de quotas ({(editingPolitique.cycles || []).length})
                  </h3>
                  <p className="text-[11px] text-slate-500 italic">
                    💡 Budget annuel = Σ(quotas × prix) sur tous les cycles
                  </p>
                </div>

                {(editingPolitique.cycles || []).map((cycle, idx) => (
                  <div
                    key={idx}
                    className="border border-blue-200 rounded-lg p-3 bg-blue-50/30 space-y-2"
                    data-testid={`cycle-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold text-blue-900">
                        📅 Cycle {idx + 1}{cycle.nom ? ` — ${cycle.nom}` : ''}
                      </div>
                      {idx > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-blue-700 text-xs h-7"
                          onClick={() => duplicateCyclePrecedent(idx)}
                          data-testid={`btn-duplicate-cycle-${idx}`}
                        >
                          🔗 Copier le cycle précédent
                        </Button>
                      )}
                    </div>

                    {/* Nom + Période */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Nom (optionnel)</Label>
                        <Input
                          value={cycle.nom || ''}
                          onChange={e => updateCycle(idx, 'nom', e.target.value)}
                          placeholder="Ex : Été, Hiver…"
                          className="h-8 text-sm"
                          data-testid={`cycle-${idx}-nom`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Mois début</Label>
                        <Select value={String(cycle.mois_debut)} onValueChange={v => updateCycle(idx, 'mois_debut', parseInt(v))}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`cycle-${idx}-mois-debut`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {MOIS_FR.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Mois fin</Label>
                        <Select value={String(cycle.mois_fin)} onValueChange={v => updateCycle(idx, 'mois_fin', parseInt(v))}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`cycle-${idx}-mois-fin`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {MOIS_FR.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Quotas par rôle */}
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { key: 'quota_permanent', label: 'Permanent', color: 'blue', countKey: 'terrain_permanent' },
                        { key: 'quota_saisonnier', label: 'Saisonnier', color: 'amber', countKey: 'terrain_saisonnier' },
                        { key: 'quota_sous_traitant', label: 'Sous-trait.', color: 'emerald', countKey: 'sous_traitant' },
                        { key: 'quota_admin', label: 'Admin', color: 'purple', countKey: 'admin' },
                      ].map(({ key, label, color, countKey }) => {
                        const nb = employeeCounts[countKey] || 0;
                        const prix = parseFloat(editingPolitique.prix_unitaire) || 0;
                        const q = parseInt(cycle[key]) || 0;
                        const total = q * prix * nb;
                        return (
                          <div key={key} className={`rounded border border-${color}-200 bg-white p-1.5`}>
                            <Label className={`text-[10px] text-${color}-700 uppercase font-semibold`}>{label}</Label>
                            <Input
                              type="number" min={0}
                              value={q}
                              onChange={e => updateCycle(idx, key, parseInt(e.target.value) || 0)}
                              className="text-center font-bold h-7 mt-0.5 text-sm"
                              data-testid={`cycle-${idx}-${key}`}
                            />
                            <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                              {nb}× → <strong className={`text-${color}-700`}>{total.toFixed(0)}$</strong>
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Saisonnier : choix reset mensuel vs unique */}
                    {editingPolitique.frequence === 'saisonnier' && (
                      <div className="flex items-center gap-3 bg-blue-50 rounded px-2 py-1.5">
                        <Label className="text-xs flex-1">Type de remise à zéro :</Label>
                        <label className="text-xs flex items-center gap-1">
                          <input
                            type="radio"
                            checked={cycle.reset_par_cycle === 'unique'}
                            onChange={() => updateCycle(idx, 'reset_par_cycle', 'unique')}
                            data-testid={`cycle-${idx}-reset-unique`}
                          />
                          Une fois par cycle
                        </label>
                        <label className="text-xs flex items-center gap-1">
                          <input
                            type="radio"
                            checked={cycle.reset_par_cycle === 'mensuel'}
                            onChange={() => updateCycle(idx, 'reset_par_cycle', 'mensuel')}
                            data-testid={`cycle-${idx}-reset-mensuel`}
                          />
                          Chaque mois dans la saison
                        </label>
                      </div>
                    )}

                    {/* Date début + notes */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Date de remise (texte libre)</Label>
                        <Input
                          value={cycle.date_debut || ''}
                          onChange={e => updateCycle(idx, 'date_debut', e.target.value)}
                          placeholder="Ex : 1er mai"
                          className="h-8 text-sm"
                          data-testid={`cycle-${idx}-date-debut`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Notes du cycle</Label>
                        <Input
                          value={cycle.notes || ''}
                          onChange={e => updateCycle(idx, 'notes', e.target.value)}
                          placeholder="Notes spécifiques…"
                          className="h-8 text-sm"
                          data-testid={`cycle-${idx}-notes`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes globales */}
              <div>
                <Label>Notes / Instructions (global)</Label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={2}
                  value={editingPolitique.notes || ''}
                  onChange={e => updateField('notes', e.target.value)}
                  placeholder="Ex : Réservé aux peintres extérieurs, taille standard XL..."
                  data-testid="dialog-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex flex-row items-center justify-between gap-2">
            {!editingPolitique?.__isNew && editingPolitique && (
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(editingPolitique)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="dialog-btn-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={closeDialog}>Annuler</Button>
              <Button onClick={savePolitique} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="dialog-btn-save">
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Sauvegarde…' : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation suppression */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Vous êtes sur le point de supprimer la politique <strong>« {confirmDelete?.vetement_nom} »</strong>.
            </p>
            <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 space-y-1">
              <p>• L'item ne sera plus visible dans le catalogue des nouveaux quotas.</p>
              <p>• <strong>L'historique des distributions est conservé.</strong></p>
              <p>• Les quotas existants des employés restent intacts (lecture seule).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button onClick={deletePolitique} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Suppression…' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolitiquesVetements;
